import { Injectable, NgZone } from "@angular/core";
import { Subject } from "rxjs";
import { NotificationChannelWebPushSubscriptionModel } from "app/models/notificationChannel.model";

export type BrowserNotificationMessage = {
    title?: string;
    body?: string;
    url?: string;
    severity?: "alarm" | "warning" | "success" | "info";
    language?: string;
    project?: string;
    alarm?: string;
    device?: string;
    timestamp?: string;
};

@Injectable({ providedIn: "root" })
export class BrowserNotificationService {
    private readonly registrationScope = "/";
    private readonly workerUrl = "/notification-worker.js";
    private readonly messageSubject = new Subject<BrowserNotificationMessage>();
    private readonly broadcastChannelName = "emma-browser-notifications";
    private broadcastChannel?: BroadcastChannel;

    readonly messages$ = this.messageSubject.asObservable();

    constructor(private readonly ngZone: NgZone) {
        this.listenToBroadcastChannel();
        this.listenToServiceWorkerMessages();
    }

    isSupported(): boolean {
        return (
            typeof window !== "undefined" &&
            "Notification" in window &&
            "serviceWorker" in navigator &&
            "PushManager" in window
        );
    }

    getPermissionState(): NotificationPermission | "unsupported" {
        if (!this.isSupported()) {
            return "unsupported";
        }

        return Notification.permission;
    }

    hasGrantedPermission(): boolean {
        return this.getPermissionState() === "granted";
    }

    async subscribe(publicKey: string): Promise<NotificationChannelWebPushSubscriptionModel> {
        if (!this.isSupported()) {
            throw new Error("This browser does not support push notifications");
        }
        if (!publicKey) {
            throw new Error("Web push is not configured");
        }

        const registration = await this.ensureRegistration();
        let subscription = await registration.pushManager.getSubscription();
        if (subscription) {
            return this.toModel(subscription);
        }

        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
            throw new Error("Notification permission was not granted");
        }

        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey:
                this.urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
        });

        return this.toModel(subscription);
    }

    private listenToBroadcastChannel(): void {
        if (typeof BroadcastChannel === "undefined") {
            return;
        }

        this.broadcastChannel = new BroadcastChannel(this.broadcastChannelName);
        this.broadcastChannel.addEventListener("message", (event) => {
            this.forwardMessage(event.data);
        });
    }

    private listenToServiceWorkerMessages(): void {
        if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
            return;
        }

        navigator.serviceWorker.addEventListener("message", (event) => {
            this.forwardMessage(event.data);
        });
    }

    private forwardMessage(data: {
        type?: string;
        payload?: BrowserNotificationMessage;
    }): void {
        if (data?.type !== "web-push-notification") {
            return;
        }

        this.ngZone.run(() => {
            this.messageSubject.next(data.payload ?? {});
        });
    }

    private async ensureRegistration(): Promise<ServiceWorkerRegistration> {
        const registration = await navigator.serviceWorker.register(
            this.workerUrl,
            {
                scope: this.registrationScope,
            },
        );

        await registration.update();

        if (registration.active) {
            return registration;
        }

        return navigator.serviceWorker.ready;
    }

    private toModel(subscription: PushSubscription): NotificationChannelWebPushSubscriptionModel {
        const value = subscription.toJSON();
        return {
            endpoint: value.endpoint,
            expirationTime:
                typeof value.expirationTime === "number"
                    ? value.expirationTime
                    : undefined,
            keys: {
                auth: value.keys?.auth ?? "",
                p256dh: value.keys?.p256dh ?? "",
            },
        };
    }

    private urlBase64ToUint8Array(base64String: string): Uint8Array {
        const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, "+")
            .replace(/_/g, "/");
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let index = 0; index < rawData.length; index += 1) {
            outputArray[index] = rawData.charCodeAt(index);
        }

        return outputArray;
    }
}
